import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, ImageOff } from "lucide-react";
import { resolveMediaUrl } from "@/lib/api";
import { SkeletonGrid } from "@/components/Skeleton";
import { useLang } from "@/i18n/LangContext";

export default function Gallery({ images = [], loading = false }) {
    const { t } = useLang();
    const [active, setActive] = useState(null); // index

    const open = (i) => setActive(i);
    const close = () => setActive(null);
    const next = () => setActive((i) => (i === null ? null : (i + 1) % images.length));
    const prev = () =>
        setActive((i) =>
            i === null ? null : (i - 1 + images.length) % images.length
        );

    return (
        <section
            id="galeria"
            data-testid="gallery-section"
            className="relative py-24 md:py-32"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
                    <div>
                        <div className="label-eyebrow mb-4">{t("02 - Galeria")}</div>
                        <h2 className="font-display uppercase text-4xl md:text-5xl lg:text-6xl tracking-tighter text-white">
                            {t("Spojrzenie z kabiny")}
                        </h2>
                    </div>
                    <p className="text-base text-[color:var(--text-secondary)] max-w-md">
                        {t("Screeny prosto z trasy. Kliknij obraz, aby otworzyć podgląd w pełnym rozmiarze.")}
                    </p>
                </div>

                {loading ? (
                    <SkeletonGrid
                        count={6}
                        className="grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
                    />
                ) : images.length === 0 ? (
                    <div
                        data-testid="gallery-empty"
                        className="panel-yellow p-16 text-center text-[color:var(--text-secondary)]"
                    >
                        <ImageOff size={28} className="mx-auto mb-3 text-[color:var(--primary)]" />
                        {t("Brak zdjęć - admin może dodać je w panelu.")}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {images.map((img, i) => (
                            <motion.button
                                key={img.id}
                                data-testid={`gallery-item-${i}`}
                                onClick={() => open(i)}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.15 }}
                                transition={{ delay: (i % 6) * 0.05, duration: 0.5 }}
                                className={`relative group overflow-hidden panel-yellow !p-0 aspect-[4/3] ${
                                    i % 5 === 0 ? "lg:row-span-2 lg:aspect-[4/5]" : ""
                                }`}
                            >
                                <img
                                    src={resolveMediaUrl(img.url)}
                                    alt={img.title || `Screen ${i + 1}`}
                                    loading="lazy"
                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--bg-base)] via-transparent to-transparent opacity-90" />
                                <div className="absolute bottom-0 left-0 right-0 p-5 text-left">
                                    {img.title && (
                                        <div className="font-display uppercase text-sm tracking-wider text-white">
                                            {t(img.title)}
                                        </div>
                                    )}
                                    {img.description && (
                                        <div className="text-xs text-[color:var(--text-secondary)] mt-1">
                                            {t(img.description)}
                                        </div>
                                    )}
                                </div>
                            </motion.button>
                        ))}
                    </div>
                )}
            </div>

            <AnimatePresence>
                {active !== null && (
                    <motion.div
                        data-testid="lightbox"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={close}
                        className="fixed inset-0 z-[100] bg-[color:var(--bg-base)]/95 backdrop-blur-md flex items-center justify-center p-6"
                    >
                        <button
                            data-testid="lightbox-close"
                            className="absolute top-6 right-6 text-white/80 hover:text-[color:var(--primary)] p-2"
                            onClick={close}
                            aria-label="Zamknij"
                        >
                            <X size={28} />
                        </button>
                        <button
                            data-testid="lightbox-prev"
                            className="absolute left-6 top-1/2 -translate-y-1/2 text-white/80 hover:text-[color:var(--primary)] p-3"
                            onClick={(e) => {
                                e.stopPropagation();
                                prev();
                            }}
                            aria-label="Poprzednie"
                        >
                            <ChevronLeft size={36} />
                        </button>
                        <button
                            data-testid="lightbox-next"
                            className="absolute right-6 top-1/2 -translate-y-1/2 text-white/80 hover:text-[color:var(--primary)] p-3"
                            onClick={(e) => {
                                e.stopPropagation();
                                next();
                            }}
                            aria-label="Następne"
                        >
                            <ChevronRight size={36} />
                        </button>
                        <motion.div
                            key={active}
                            initial={{ scale: 0.96, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 0.25 }}
                            className="max-w-6xl w-full max-h-[85vh]"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <img
                                src={resolveMediaUrl(images[active].url)}
                                alt={images[active].title}
                                className="w-full h-full object-contain max-h-[85vh] rounded-md"
                            />
                            <div className="mt-4 text-center">
                                <div className="font-display uppercase tracking-wider text-white">
                                    {t(images[active].title)}
                                </div>
                                {images[active].description && (
                                    <div className="text-sm text-[color:var(--text-secondary)] mt-1">
                                        {t(images[active].description)}
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
}
